import { Link } from 'react-router-dom';
import {
  dateLabel, dateLabelLong, today, REQUEST_CATEGORY_LABEL,
  VISIT_STATUS_LABEL, VISIT_STATUS_TONE, INCIDENT_STATUS_LABEL, INCIDENT_STATUS_TONE,
  type IncidentStatus, type OvertimeStatus, type RequestCategory, type VisitStatus,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { Async, Badge, Empty } from '@/components/ui';
import { Icon, type IconName } from '@/components/common/NavIcons';

interface EmployeeHomeData {
  visits: {
    id: string; scheduled_date: string; scheduled_start: string | null; scheduled_end: string | null;
    status: VisitStatus; unit_code: string; unit_name: string | null; address: string | null;
  }[];
  overtime: { id: string; work_date: string; hours: number; status: OvertimeStatus; amount_minor: number | null }[];
  vacations: { id: string; start_date: string; end_date: string; days: number; status: string }[];
  incidents: { id: string; title: string; status: IncidentStatus; unit_code: string; created_at: string }[];
}

interface StaffRequest {
  id: string; category: RequestCategory; note: string | null; priority: string;
  status: string; created_at: string; unit_code: string | null; unit_name: string | null;
}

/* El ícono por familia de solicitud: la emergencia alarma, los insumos son
   inventario, y el resto es servicio. Mismo mapeo que usa el portal del
   cliente al pedir (`portal/Services.tsx`) — las dos puntas del mismo flujo
   se dibujan igual. */
const REQUEST_ICON = (cat: RequestCategory): IconName =>
  cat === 'EMERGENCY' ? 'incidencias' : cat === 'SUPPLIES' ? 'inventario' : 'servicios';

/** `16 set`, para leerse de un vistazo en una tarjeta chica. */
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic'];
const diaCorto = (iso: string): string =>
  `${Number(iso.slice(8, 10))} ${MESES_CORTOS[Number(iso.slice(5, 7)) - 1]}`;

/** La hora local si fue hoy; el día si fue antes. */
function cuandoLlego(createdAt: string): string {
  const fecha = createdAt.slice(0, 10);
  if (fecha !== today()) return dateLabel(fecha);
  return new Date(createdAt).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Las solicitudes vivas de los consultorios, arriba de todo: si un cliente
 * está pidiendo algo, eso va antes que la agenda (pedido del dueño,
 * 29-ago-2026). La sección entera desaparece cuando no hay nada.
 */
function SolicitudesDeClientes() {
  const query = useApi<{ requests: StaffRequest[] }>('/employee/requests');
  const voy = useApiMutation(
    (id: string) => api.post(`/employee/requests/${id}/acknowledge`),
    ['/employee/requests'],
  );

  const requests = query.data?.requests ?? [];
  if (requests.length === 0) return null;

  return (
    <section className="msec">
      <div className="msec__title">Solicitudes de clientes</div>
      {requests.map((r) => {
        const urgente = r.category === 'EMERGENCY';
        const tomada = r.status === 'ACKNOWLEDGED';
        return (
          <div key={r.id} className={`mcard solicitud${urgente ? ' solicitud--urgente' : ''}`}>
            <div className="solicitud__fila">
              <span className={`solicitud__ico${urgente ? ' solicitud__ico--urgente' : ''}`} aria-hidden="true">
                <Icon name={REQUEST_ICON(r.category)} size={20} />
              </span>
              <span className="solicitud__txt">
                <strong>
                  {REQUEST_CATEGORY_LABEL[r.category]}
                  {/* La urgencia también en PALABRAS: el color solo no alcanza. */}
                  {urgente && <span className="solicitud__urgencia"> · URGENTE</span>}
                </strong>
                <span className="solicitud__detalle">
                  {r.unit_code ?? 'Sin consultorio'} · {cuandoLlego(r.created_at)}
                </span>
                {r.note && <span className="solicitud__detalle">{r.note}</span>}
              </span>
              {tomada ? (
                <span className="solicitud__tomada">En camino</span>
              ) : (
                <button type="button" className="btn btn--primary solicitud__voy"
                  disabled={voy.isPending}
                  onClick={() => voy.mutate(r.id)}>
                  Voy
                </button>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}

export function EmployeeHome() {
  const query = useApi<EmployeeHomeData>('/employee/home');
  const now = today();

  return (
    <Async query={query}>
      {(data) => {
        const todays = data.visits.filter((v) => v.scheduled_date === now);
        const upcoming = data.visits.filter((v) => v.scheduled_date > now);
        const pendingOvertime = data.overtime.filter(
          (o) => o.status === 'REPORTED' || o.status === 'PENDING_APPROVAL',
        );

        /* El dato vivo de cada tarjeta: una línea que diga en qué está lo mío,
           sin entrar. Vacaciones muestra la reserva que viene; horas, lo que
           espera aprobación o lo último reportado. */
        const vacacionViva = data.vacations
          .filter((v) => v.status === 'REQUESTED' || v.status === 'APPROVED')
          .sort((a, b) => a.start_date.localeCompare(b.start_date))[0];
        const subVacaciones = vacacionViva
          ? `${vacacionViva.days} ${vacacionViva.days === 1 ? 'día' : 'días'} `
            + `${vacacionViva.status === 'APPROVED' ? 'aprobados' : 'pendientes'} `
            + `${diaCorto(vacacionViva.start_date)}–${diaCorto(vacacionViva.end_date)}`
          : 'Reservá tus días de descanso';
        const ultimaHora = data.overtime[0];
        const subHoras = pendingOvertime.length > 0
          ? `${pendingOvertime.length} ${pendingOvertime.length === 1 ? 'reporte esperando' : 'reportes esperando'} aprobación`
          : ultimaHora
            ? `Última: ${diaCorto(ultimaHora.work_date)} · ${ultimaHora.hours} h`
            : 'Todavía no reportaste horas';

        /* En escritorio, dos columnas: a la izquierda lo que pasa (solicitudes,
           hoy, próximos); a la derecha «Lo mío» y las incidencias, que antes
           quedaban bajo el fold. En el teléfono las columnas se disuelven y el
           orden es el de siempre (ver `.pantalla-split` en personal.css). */
        return (
          <div className="msec--split pantalla-split">
            <div className="pantalla-split__main">
              <SolicitudesDeClientes />

              <section className="msec">
                <div className="msec__title">Hoy · {dateLabelLong(now)}</div>
                {todays.length === 0 ? (
                  <div className="mcard">
                    <Empty icon="inicio" title="Hoy no tenés visitas" hint="Si te asignan una, aparece acá." />
                  </div>
                ) : (
                  todays.map((v) => (
                    <Link key={v.id} to={`/empleado/visitas/${v.id}`} className="mcard" style={{ display: 'block' }}>
                      <div className="mrow" style={{ paddingTop: 0 }}>
                        <span>
                          <strong>{v.unit_code}</strong>
                          <span className="msub">{v.unit_name ?? v.address ?? ''}</span>
                        </span>
                        <Badge tone={VISIT_STATUS_TONE[v.status]}>{VISIT_STATUS_LABEL[v.status]}</Badge>
                      </div>
                      <div className="mrow" style={{ borderBottom: 0, paddingBottom: 0 }}>
                        <span className="mrow__l">Horario</span>
                        <span className="mrow__r mono">
                          {v.scheduled_start ?? '—'}{v.scheduled_end ? `–${v.scheduled_end}` : ''}
                        </span>
                      </div>
                    </Link>
                  ))
                )}
              </section>

              {upcoming.length > 0 && (
                <section className="msec">
                  <div className="msec__title">Los próximos días</div>
                  <div className="mcard mcard--par">
                    {upcoming.map((v, i) => (
                      <div key={v.id} className="mrow"
                        style={i === upcoming.length - 1 ? { borderBottom: 0 } : undefined}>
                        <span className="mrow__l">
                          {v.scheduled_date.slice(5)} · {v.unit_code}
                        </span>
                        <span className="mrow__r mono">{v.scheduled_start ?? ''}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            <div className="pantalla-split__lateral msec--split-lateral">
              {/* «Más gráfica y alusiva» (dueño, 29-ago-2026): estas dejaron de
                  ser listas de renglones y son tarjetas con ícono, como las del
                  centro de mejora del portal. El detalle vive en su pantalla;
                  acá va el ícono, el nombre y UNA línea con el dato vivo. */}
              <section className="msec">
                <div className="msec__title">Lo mío</div>
                <Link to="/empleado/horas" className="mejora-card mejora-card--link">
                  <span className="mejora-card__ico"><Icon name="horas" size={20} /></span>
                  <span className="mejora-card__txt">
                    <strong>Horas extra</strong>
                    <span>{subHoras}</span>
                  </span>
                </Link>
                <Link to="/empleado/vacaciones" className="mejora-card mejora-card--link">
                  <span className="mejora-card__ico"><Icon name="vacaciones" size={20} /></span>
                  <span className="mejora-card__txt">
                    <strong>Vacaciones</strong>
                    <span>{subVacaciones}</span>
                  </span>
                </Link>
                <Link to="/empleado/insumos" className="mejora-card mejora-card--link">
                  <span className="mejora-card__ico"><Icon name="inventario" size={20} /></span>
                  <span className="mejora-card__txt">
                    <strong>Insumos</strong>
                    <span>Registrá lo que llegó o se usó</span>
                  </span>
                </Link>
              </section>

              {data.incidents.length > 0 && (
                <section className="msec">
                  <div className="msec__title">Incidencias asignadas</div>
                  {data.incidents.map((i) => (
                    <div key={i.id} className="mcard">
                      <div className="mrow" style={{ paddingTop: 0, borderBottom: 0 }}>
                        <span>
                          <strong>{i.title}</strong>
                          <span className="msub">{i.unit_code}</span>
                        </span>
                        <Badge tone={INCIDENT_STATUS_TONE[i.status] ?? 'warn'}>
                          {INCIDENT_STATUS_LABEL[i.status] ?? i.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </section>
              )}
            </div>
          </div>
        );
      }}
    </Async>
  );
}
